import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';
import _ from 'lodash';

import {
  ShopOrder,
  ShopOrderCancel,
  ShopOrderCreateDto,
  ShopOrderManualRefundTarget,
  ShopOrderUpdate,
  StoreRole,
} from './shopOrder.interface';

const vaildRolesCreateShopOrder = [LowerRole.StoreCustomer, LowerRole.StoreGuest];

export const createShopOrder = async (
  shopOrderDto: ShopOrderCreateDto,
  storeRole: StoreRole,
) => {
  if (!_.includes(vaildRolesCreateShopOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    const postShopOrderResponse = await api_.post<ShopOrder>(
      `/${storeRole}/shop-order/create`,
      shopOrderDto,
    );
    return postShopOrderResponse;
  }
};

export const verifyShopOrderById = async (
  id: string,
  storeRole: StoreRole,
  receiptId?: string,
) => {
  if (!_.includes(vaildRolesCreateShopOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    const { data: isVerify } = await api_.get<boolean>(
      `${storeRole}/shop-order/success/${id}?${queryString({ receiptId })}`,
    );
    return isVerify;
  }
};

export const deleteShopOrderById = async (id: string, storeRole: StoreRole) => {
  if (!_.includes(vaildRolesCreateShopOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    return await api_.delete<ShopOrder>(`${storeRole}/shop-order/delete/${id}`);
  }
};

//* shopOrder update
export const updateShopOrder = async (
  shopOrderUpdate: ShopOrderUpdate,
  role: LowerRole,
) => {
  if (role === LowerRole.Admin) {
    const { data } = await api_.patch(
      `/${role}/shop-order/${shopOrderUpdate._id}`,
      shopOrderUpdate,
    );
    return { data };
  } else {
    const { data } = await api_.patch(
      `/${role}/shop-order/patch-detail/${shopOrderUpdate._id}`,
      shopOrderUpdate,
    );
    return { data };
  }
};

//* 주문 알림 재발송 (입금안내/결제완료/배송완료) — 게스트 번호 교정 후 재발송 용도
//* 발송 함수가 내부에서 user를 다시 populate하므로 교정된 번호로 나간다.
//* waiting: Waiting(전화주문)=고정계좌 안내 / NotPaid+vBank=저장된 가상계좌 안내, 그 외 상태 400.
//* paid: Paid/Confirmed만, delivered: Delivered만, 취소 주문은 400. 구서버 미배포 시 404.
//* smsOnly(waiting 전용): 미지정(=백엔드 기본 true)이면 계좌 SMS만, false면 주문내역 LMS까지 함께 나간다.
//* 문구(보내는분·조의문구)는 주문내역 LMS에만 들어가므로, 문구를 고친 뒤엔 반드시 false로 보내야 한다.
export type ShopOrderAlertType = 'waiting' | 'paid' | 'delivered';

export const resendShopOrderAlert = async (
  shopOrderId: string,
  type: ShopOrderAlertType,
  role: LowerRole,
  smsOnly?: boolean,
) => {
  const { data } = await api_.post(`${role}/shop-order/resend-alert/${shopOrderId}`, {
    type,
    ...(smsOnly === undefined ? {} : { smsOnly }),
  });
  return data;
};

//* admin 용 주문 상품 교체 — 결제 후 상위 상품으로 변경하고 차액을 수기 수취한 경우의 정정 처리
//* 백엔드가 orderDetail.shopItem + shopItemEmbed(생성 시와 동일 매핑) + paymentDetail.paymentByCash(+=차액)를
//* 원자적으로 갱신한다. 구서버 미배포 시 404. (스펙: docs/shop-order-change-item-spec.md)
export interface ShopOrderItemChange {
  shopItemId: string; // 교체할 shopItem._id (주문의 bugoBrand·shop에서 판매 가능한 상품)
  additionalPaymentByCash: number; // 추가로 받은 차액(원), 0 이상
  memo?: string; // 수취 경위 메모 (ex. "가상계좌 이체 35,000원 수취")
}

export const adminChangeShopOrderItem = async (
  shopOrderId: string,
  dto: ShopOrderItemChange,
) => {
  const { data } = await api_.patch<ShopOrder>(
    `admin/shop-order/change-item/${shopOrderId}`,
    dto,
  );
  return data;
};

//* ───── 면세계산서 발행 요청 · 수기환불(계좌이체) 요청 — seller 접수 → admin 처리 대기열 ─────
//* 가상계좌·무통장(계좌 입금) 결제 전용. 상태머신: 없음 → Requested → Done|Rejected.
//* Requested/Rejected 상태에선 재요청이 기존 요청을 덮어쓰고(오타 교정), Done이면 400.
//* 처리(PATCH)는 admin 전용 — Requested에서만 가능, 경합 시 409. 구서버 미배포 시 404.
//* (스펙: docs/shop-order-seller-request-spec.md)

export interface ShopOrderTaxInvoiceRequestDto {
  bizRegImage?: string; // 선업로드한 image._id (postImage) — 사진만 오는 경우도 수용
  bizNumber?: string;
  email?: string; // 발행본 수신 이메일
  requestNote?: string;
}

export const requestShopOrderTaxInvoice = async (
  shopOrderId: string,
  dto: ShopOrderTaxInvoiceRequestDto,
  role: LowerRole,
) => {
  const { data } = await api_.post<ShopOrder>(
    `${role}/shop-order/tax-invoice-request/${shopOrderId}`,
    dto,
  );
  return data;
};

export interface ShopOrderManualRefundRequestDto {
  account: { bank: string; accountNumber: string; holderName: string };
  target?: ShopOrderManualRefundTarget; // 기본 Customer
  amount?: number; // 기본 paymentDetail.paymentByCash, 1 이상 정수
  requestNote?: string;
}

export const requestShopOrderManualRefund = async (
  shopOrderId: string,
  dto: ShopOrderManualRefundRequestDto,
  role: LowerRole,
) => {
  const { data } = await api_.post<ShopOrder>(
    `${role}/shop-order/manual-refund-request/${shopOrderId}`,
    dto,
  );
  return data;
};

export type ShopOrderSellerRequestKind = 'taxInvoiceRequest' | 'manualRefund';

export interface ShopOrderSellerRequestProcessDto {
  status: 'Done' | 'Rejected';
  cashReceiptCanceled?: boolean; // 면세계산서 전용 — true면 cashReceiptCanceledAt 기록
  resultNote?: string; // 국세청 승인번호 / 이체 메모 / 반려 사유
}

export const processShopOrderSellerRequest = async (
  shopOrderId: string,
  kind: ShopOrderSellerRequestKind,
  dto: ShopOrderSellerRequestProcessDto,
) => {
  const path =
    kind === 'taxInvoiceRequest' ? 'tax-invoice-request' : 'manual-refund-request';
  const { data } = await api_.patch<ShopOrder>(
    `admin/shop-order/${path}/${shopOrderId}`,
    dto,
  );
  return data;
};

//* 처리 되돌리기 — 실수로 완료/반려 처리한 건을 다시 Requested 대기로 (admin 전용).
//* 백엔드가 부수효과를 정확히 역전한다: 계좌환불 Done이 취소확정까지 했던 건
//* (manualRefund.cancelFinalized)이면 주문 Canceled→CancelRequested 복귀 + 포인트 회수.
//* 도장만 찍힌 Done은 도장만 제거. 이미 나간 취소알림은 회수 불가(재처리 시 재발송).
//* 이미 대기·요청 없음은 400, 경합 409, 구서버 400/404 → 호출부에서 안내.
//* (스펙: docs/shop-order-seller-request-spec.md · "처리 되돌리기")
export const revertShopOrderSellerRequest = async (
  shopOrderId: string,
  kind: ShopOrderSellerRequestKind,
) => {
  const path =
    kind === 'taxInvoiceRequest' ? 'tax-invoice-request' : 'manual-refund-request';
  const { data } = await api_.patch<ShopOrder>(
    `admin/shop-order/${path}/${shopOrderId}`,
    { status: 'Requested' },
  );
  return data;
};

export const cancelShopOrder = async (shopOrder: ShopOrderCancel, role: LowerRole) => {
  const { data } = await api_.patch(
    `${role}/shop-order/cancel/${shopOrder._id}`,
    shopOrder,
  );
  return { data };
};

//* store-seller용 전화주문생성
export const createWaitingOrder = async (
  shopOrder: ShopOrderCreateDto,
  role: LowerRole,
) => {
  const { data } = await api_.post<ShopOrder>(
    `${role}/shop-order/create-waiting`,
    shopOrder,
  );
  return data;
};

//* store-seller용 입금완료처리
export const patchOrderWaitingToPaid = async (shopOrderId: string, role: LowerRole) => {
  const { data } = await api_.patch<ShopOrder>(
    `${role}/shop-order/waiting-paid/${shopOrderId}`,
  );
  return data;
};

//* 주문 소프트 삭제 (판매자 콘솔) — 결제 전(NotPaid/Waiting)·취소완료 주문만.
//* 문서는 남고 목록에서만 빠진다. admin/store-seller 라우트 모양이 같다.
const sellerRolesForSoftDelete = [LowerRole.Admin, LowerRole.StoreSeller];

export const softDeleteShopOrderForSeller = async (
  shopOrderId: string,
  role: LowerRole,
  reason?: string,
) => {
  if (!_.includes(sellerRolesForSoftDelete, role)) {
    throw new Error(`${role} role can't use this api`);
  }
  const { data } = await api_.delete<ShopOrder>(
    `/${role}/shop-order/soft-delete/${shopOrderId}`,
    { data: { reason } },
  );
  return data;
};

//* 삭제한 주문 검색 — 주문자(보내는 분)·수령인 번호 뒷자리로도 찾는다(숫자 4자리 이상).
//* 삭제 주문은 일반 목록 조회에서 빠지므로 이 경로로만 조회된다.
export const searchDeletedShopOrderListForSeller = async (
  phoneNumber: string,
  role: LowerRole,
) => {
  if (!_.includes(sellerRolesForSoftDelete, role)) {
    throw new Error(`${role} role can't use this api`);
  }
  const { data } = await api_.get<ShopOrder[]>(
    `/${role}/shop-order/deleted/search?${queryString({ phoneNumber })}`,
  );
  return data;
};

//* ───── 전화 응대용 주문 검색 (살아있는 주문) ─────
//* 전화 온 번호로 그 사람이 만들던/결제한 주문을 찾아 주문자 정보·문구를 그대로 재사용하기 위한 조회.
//* 별도 엔드포인트 없이 seller 제네릭 find를 쓴다 — 서버가 자기 shop 주문으로만 스코프를 좁히므로
//* (owningFilter: shop.users ∋ 나) 남의 꽃집 주문은 애초에 나오지 않는다. 삭제 주문도 서버가 제외한다.
//* 다른 꽃집 주문까지 보려면 전용 엔드포인트가 필요하다 (docs/shop-order-phone-lookup-spec.md).

export const SHOP_ORDER_PHONE_SEARCH_MIN_DIGITS = 4;
const SHOP_ORDER_PHONE_SEARCH_LIMIT = 20;

//* 저장 형식이 '01012345678'/'010-1234-5678'로 섞여 있어 숫자 사이 하이픈을 허용하고
//* 끝자리 기준($)으로 맞춘다 — 뒷 4자리 검색을 위해. 숫자만 넣으므로 정규식 인젝션이 없다.
//* (백엔드 삭제주문 검색 buildPhoneNumberRegex와 같은 규칙)
export const buildPhoneNumberSearchPattern = (phoneNumber: string): string | null => {
  const digits = (phoneNumber ?? '').replace(/\D/g, '');
  if (digits.length < SHOP_ORDER_PHONE_SEARCH_MIN_DIGITS) return null;
  return `${digits.split('').join('-?')}$`;
};

export const searchShopOrderListByPhoneForSeller = async (
  phoneNumber: string,
  role: LowerRole,
) => {
  if (!_.includes(sellerRolesForSoftDelete, role)) {
    throw new Error(`${role} role can't use this api`);
  }
  const pattern = buildPhoneNumberSearchPattern(phoneNumber);
  if (!pattern) {
    throw new Error(
      `번호를 ${SHOP_ORDER_PHONE_SEARCH_MIN_DIGITS}자리 이상 입력해주세요.`,
    );
  }
  const { data } = await api_.get<ShopOrder[]>(
    `/${role}/shop-order/?${queryString({
      filter: {
        $or: [
          { 'deliveryDetail.senderPhoneNumber': { $regex: pattern } },
          { 'deliveryDetail.receiverPhoneNumber': { $regex: pattern } },
        ],
      },
      populate: [
        {
          path: 'bugo',
          populate: [
            {
              path: 'fevent',
              populate: [{ path: 'funeralHomeInfo' }, { path: 'roomInfo' }],
            },
          ],
        },
      ],
      options: {
        sort: { createdAt: -1 },
        limit: SHOP_ORDER_PHONE_SEARCH_LIMIT,
      },
    })}`,
  );
  return data;
};

//* 삭제 주문 복원 — 원래 상태(입금대기 등)로 목록에 되돌린다.
//* 입금완료로 되살릴 땐 patchOrderWaitingToPaid가 docStatus까지 복원하므로 이걸 먼저 부를 필요 없다.
export const restoreShopOrderForSeller = async (shopOrderId: string, role: LowerRole) => {
  if (!_.includes(sellerRolesForSoftDelete, role)) {
    throw new Error(`${role} role can't use this api`);
  }
  const { data } = await api_.patch<ShopOrder>(
    `/${role}/shop-order/restore/${shopOrderId}`,
  );
  return data;
};

//*admin 용 shopOrder account처리
export const accountShopOrderList = async (
  shopOrderIdList: string[],
  accountDate?: Date,
  options?: { accountedAsEtc?: boolean },
) => {
  const now = accountDate || new Date();
  const accountUpdateShopOrder: Record<string, unknown> = {
    'paymentDetail.isAccounted': true,
    'paymentDetail.accountedDate': now,
  };
  if (options?.accountedAsEtc) {
    accountUpdateShopOrder['paymentDetail.accountedAsEtc'] = true;
  }
  const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
    update: accountUpdateShopOrder,
    filter: {
      _id: { $in: shopOrderIdList },
    },
  });
  return data;
};

const CHUNK_SIZE = 1000;

//*admin 용 shopOrder rebate처리 (3000건 이상 시 load 에러 방지를 위해 1000건씩 분할 요청)
export const rebateShopOrderList = async (
  shopOrderIdList: string[],
  rebateDate?: Date,
) => {
  const now = rebateDate || new Date();
  const rebateUpdateShopOrder = {
    'paymentDetail.isRebated': true,
    'paymentDetail.rebatedDate': now,
  };

  const chunks = _.chunk(shopOrderIdList, CHUNK_SIZE);
  const results: ShopOrder[][] = [];

  for (const chunk of chunks) {
    const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
      update: rebateUpdateShopOrder,
      filter: {
        _id: { $in: chunk },
      },
    });
    results.push(Array.isArray(data) ? data : []);
  }

  return results.flat();
};

//*admin 용 shopOrder 정산취소
export const cancelAccountShopOrderList = async (shopOrderIdList: string[]) => {
  const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
    update: {
      'paymentDetail.isAccounted': false,
      'paymentDetail.accountedDate': null,
    },
    filter: {
      _id: { $in: shopOrderIdList },
    },
  });
  return data;
};

//*admin 용 shopOrder 상조정산취소
export const cancelRebateShopOrderList = async (shopOrderIdList: string[]) => {
  const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
    update: {
      'paymentDetail.isRebated': false,
      'paymentDetail.rebatedDate': null,
    },
    filter: {
      _id: { $in: shopOrderIdList },
    },
  });
  return data;
};

export const sendVBankAlimUser = async (id: string, vBank: string) => {
  const result = await api_.post(`public/shop-order/send-v-bank-message/${id}`, {
    vBank,
  });
  return result;
};

//*admin 용 shopOrder rebate처리
export const superRebateShopOrderList = async (shopOrderIdList: string[]) => {
  const now = new Date(2024, 3, 15);
  const rebateUpdateShopOrder = {
    'paymentDetail.rebatedDate': now,
  };
  const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
    update: rebateUpdateShopOrder,
    filter: {
      _id: { $in: shopOrderIdList },
    },
  });
  return data;
};
